import Backbone from 'backbone'
import optionTemplate from './wob-size-menu-option.hbs'
import i18n from 'scoreboard/components/i18n'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import positiveIntegerValidator from 'scoreboard/components/validators/positive-integer-validator'
import numericRangeValidatorFactory from 'scoreboard/components/validators/numeric-range-validator-factory'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import _ from 'lodash'
import WobSizeInputListener from './wob-size-input-listener'

var DimensionOption = Backbone.View.extend({
	className: 'wob-size-menu-option',

	events: {
		'input': '_onInput'
	},

	initialize: function () {
		this.on('slide:away', () => {
			// #25078 if the user goes "back" in the menu, reset the state of
			// the input
			this._updateInput()
			this.markedField && this.markedField.clearError()
		})
	},

	render: function () {
		this.$el.html(optionTemplate({
			label: i18n.get('dashboards.size.' + this.dimension)
		}))

		this.inputListener = new WobSizeInputListener({ el: this.$('input') })
		this.listenTo(this.inputListener, 'apply', () => this.applySize())

		this._updateInput()

		this.markedField = new MarkedField({
			el: this.$('input'),
			validator: [requiredValidator, positiveIntegerValidator, numericRangeValidatorFactory(1,10000)],
			evaluator: numericEvaluator
		}).render()

		return this
	},

	_updateInput: function () {
		// If the input is being updated by something other than direct user input
		// (either on initial render or update of the other dimension), the placeholder
		// text is always valid. It won't show if all selected wobs have the same
		// value, and it will show "multiple" if they don't
		this.togglePlaceholder(true)

		let selectedValues = _(this.canvas.selection.wobs).map(w => w.get(this.dimension)).uniq().value()

		this.$('input').val(parseInt(selectedValues.length > 1 ? '' : this.model.wob.get(this.dimension)))
	},

	_onInput: function (e) {
		// #23148 If the input is being directly updated by user input (ie typing
		// directly into this input), the "multiple" placeholder text is never valid.
		// The input will only be empty if it was manually cleared out, in which
		// case we don't want "multiple" to show
		this.togglePlaceholder(false)
	},

	applySize: function () {
		if (this.markedField.validate()) {
			return
		}

		let val = parseInt(this.markedField.val())

		if (val == this.lastValue) {
			// Resize calculations can be expensive, don't try it if nothing changed
			return
		}

		let width = this.dimension === 'width' ? val : null
		let height = this.dimension === 'height' ? val : null

		this.canvas.selection.resize(width, height)
		this.canvas.selection.wobs.forEach(w => w.trigger('config:resize'))

		this.trigger('change')

		this.lastValue = val
	},

	togglePlaceholder (show) {
		this.$('input').attr('placeholder', show ? i18n.get('dashboards.size.multiple') : '')
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.markedField, this.inputListener ], 'remove')
	}
})

function createDimensionOption (dimension, otherDimension, canvas, model) {
	let OptionView = DimensionOption.extend({ canvas, dimension, otherDimension })
	let view = new OptionView({ model })
	return {
		menuTarget: {
			label: 'dashboards.size.' + dimension,
			contentView: view
		},
		view
	}
}

/**
 * Returns the wob "Size" config menu with width and/or height input options
 *
 * @param {Object} opt
 * @param {Boolean} [options.width=false] whether the "width" input should be shown
 * @param {Boolean} [options.height=false] whether the "height" input should be shown
 * @param {WidgetModel} options.model the widget model being configured
 */
var wobSizeMenu = function (canvas, opt = {}) {
	let target = []

	let widthOption
	if (opt.width !== false) {
		widthOption = createDimensionOption('width', 'height', canvas, opt.model)
		target.push(widthOption.menuTarget)
	}

	let heightOption
	if (opt.height !== false) {
		heightOption = createDimensionOption('height', 'width', canvas, opt.model)
		target.push(heightOption.menuTarget)
	}

	if (widthOption && heightOption) {
		let addChangeListener = (listenerView, listenToView) => {
			listenerView.listenTo(listenToView, 'change', () => {
				// #17974 after the other dimension is updated, make sure this input
				// is still accurate (could have changed to keep aspect ratio the same)
				// Wait until nextTick because the other dimension may not have been
				// updated yet
				//
				// #24093 note that we're now listening for a manually triggered 'change'
				// event on the view, not the model. This is so we don't get in a cycle
				// where changing the wob width changes the wob height, which then
				// changes the width input (since it was listening for height changes).
				process.nextTick(() => listenerView._updateInput())
			})
		}

		addChangeListener(widthOption.view, heightOption.view)
		addChangeListener(heightOption.view, widthOption.view)
	}

	return {
		id: 'size',
		type: 'submenu',
		label: 'dashboards.size',
		target: target,
		width: 200
	}
}

export default wobSizeMenu

wobSizeMenu.__filename__ = 'dashboards/widgets/wob-size-menu.js'
